import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Calendar } from '@heidi/design-system/calendar';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { Skeleton } from '@heidi/design-system/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@heidi/design-system/tabs';
import {
  TypographyCaption,
  TypographyP2,
  TypographyP2Bold,
  typographyVariant,
} from '@heidi/design-system/typography';
import { MapPinIcon } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { meetingBookerDurationMessages } from '@/features/demo-flow/demo-flow.messages';
import { type TimeSlot } from './meeting-booker.types';

const DURATION_OPTIONS = [
  { value: 900000, message: meetingBookerDurationMessages.fifteenMin },
  { value: 1800000, message: meetingBookerDurationMessages.thirtyMin },
] as const;

export function MeetingCalendarPanel({
  isLoading,
  availableDates,
  selectedDate,
  onSelectDate,
  activeDuration,
  onSelectDuration,
  slots,
  timezone,
  onSelectSlot,
}: {
  isLoading: boolean;
  availableDates: Set<string>;
  selectedDate: Date | undefined;
  onSelectDate: (date: Date | undefined) => void;
  activeDuration: number;
  onSelectDuration: (duration: number) => void;
  slots: TimeSlot[];
  timezone: string;
  onSelectSlot: (slot: TimeSlot) => void;
}) {
  const intl = useIntl();

  if (isLoading) {
    return (
      <div className="flex gap-4 p-4">
        <Skeleton className="size-64" />
        <Skeleton className="h-64 w-36" />
      </div>
    );
  }

  return (
    <div className="flex overflow-hidden rounded-lg border border-border bg-surface shadow-md">
      {/* Calendar */}
      <div className="p-3">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={onSelectDate}
          disabled={(date) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return date < today || !availableDates.has(date.toDateString());
          }}
          fixedWeeks
          className="p-0"
        />
      </div>

      {/* Dark sidebar */}
      <div className="flex w-40 flex-col gap-4 rounded-r-lg bg-background-primary px-3 py-4">
        {/* Duration */}
        <div className="flex flex-col gap-2">
          <span
            className={cn(
              typographyVariant({ variant: 'captionBold' }),
              'text-text-primary-invert'
            )}
          >
            <FormattedMessage id="tVl8fpLPk1" defaultMessage="How long do you need?" />
          </span>
          <Tabs
            value={String(activeDuration)}
            onValueChange={(v) => onSelectDuration(Number(v))}
          >
            <TabsList className="h-8 w-full">
              {DURATION_OPTIONS.map((opt) => (
                <TabsTrigger
                  key={opt.value}
                  value={String(opt.value)}
                  className="flex-1 px-2 py-1"
                >
                  <FormattedMessage {...opt.message} />
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Time slots */}
        {selectedDate && (
          <div className="flex flex-col gap-2">
            <span
              className={cn(
                typographyVariant({ variant: 'captionBold' }),
                'text-text-primary-invert'
              )}
            >
              <FormattedMessage id="Wlyvmn/1on" defaultMessage="What time works best?" />
            </span>
            <ScrollArea className="-mr-1 max-h-44 pr-1">
              <div className="flex flex-col gap-1.5">
                {slots.map((slot) => (
                  <Button
                    key={slot.startMillisUtc}
                    variant="outline"
                    size="sm"
                    className="border-text-primary-invert/20 bg-transparent text-text-primary-invert hover:bg-text-primary-invert/10 hover:text-text-primary-invert"
                    onClick={() => onSelectSlot(slot)}
                  >
                    {intl.formatTime(new Date(slot.startMillisUtc), {
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZone: timezone,
                    })}
                  </Button>
                ))}
                {slots.length === 0 && (
                  <TypographyCaption className="py-2 text-center text-text-primary-invert/60">
                    <FormattedMessage
                      id="9gWhaFgzlk"
                      defaultMessage="No times available"
                    />
                  </TypographyCaption>
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>
    </div>
  );
}

export function DemoDetails({
  selectedSlot,
  timezone,
}: {
  selectedSlot: TimeSlot;
  timezone: string;
}) {
  const intl = useIntl();
  const slotDate = new Date(selectedSlot.startMillisUtc);

  const dateStr = intl.formatDate(slotDate, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: timezone,
  });

  const timeStr = intl.formatTime(slotDate, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  });

  return (
    <div className="flex flex-col gap-1">
      <TypographyCaption className="text-text-secondary">
        <FormattedMessage id="/rwFJVNZeo" defaultMessage="Your demo details:" />
      </TypographyCaption>
      <TypographyP2Bold>
        <FormattedMessage
          id="syXx7WqrZU"
          defaultMessage="{date}, {time}"
          values={{ date: dateStr, time: timeStr }}
          description="Your demo details: {date}, {time}"
        />
      </TypographyP2Bold>
      <div className="flex items-center gap-1">
        <MapPinIcon className="size-3 text-text-secondary" />
        <TypographyP2 className="text-text-secondary">
          <FormattedMessage id="crZ+Bimh7N" defaultMessage="Google Meet" />
        </TypographyP2>
      </div>
    </div>
  );
}
